/**
 * The client side of tap-to-sticker (07 §2).
 *
 * Owns one worker, one photo's embedding at a time, and the honest fallback: if the model
 * cannot load — an old browser, no WASM, a device that runs out of memory — the caller drops
 * to the brush and the product still works. 07 §8 is explicit that this has to degrade rather
 * than fail, because "no camera permission" and "eight-year-old Android" are both real users.
 *
 * Masks come back as a single-channel alpha at photo resolution. They are turned into a PNG
 * here rather than in the worker so the worker stays a pure model runner and the canvas can
 * draw the same buffer it uploads.
 */

import type { SegmentResponse } from '../workers/segment.worker'

export interface SegmentPoint {
  x: number
  y: number
  include: boolean
}

interface SegmentResult {
  /** Single-channel alpha, `width * height` bytes, 0 or 255. */
  alpha: Uint8Array
  width: number
  height: number
  score: number
  ms: number
}

export type SegmentState = 'idle' | 'loading' | 'ready' | 'encoding' | 'unavailable'

/**
 * Phones and tablets never run the encoder locally, WebGPU or not.
 *
 * The 2026-08-22 QA pass measured one automatic encode at 2.4-3.5 GB of renderer RSS in WASM
 * and gated on WebGPU - and the same evening a real iPhone on iOS 18.7 (which HAS WebGPU)
 * crash-reloaded every 2-4 seconds the moment a photo landed on the canvas: WKWebView's
 * memory ceiling is a fraction of a desktop's, fp16-on-WebGPU included. The reload re-ran
 * the eager encode, so one crash became a storm that read as "the app refreshes in a loop"
 * and ended in Safari's repeated-problem page. Origin logs: 79 requests, the last minute of
 * them GET /w/<id> at up to twice per second.
 *
 * iPadOS masquerades as macOS, so the touch-point check is load-bearing, not belt-and-braces.
 */
export function isMobileDevice(
  userAgent: string = navigator.userAgent,
  touchPoints: number = navigator.maxTouchPoints,
): boolean {
  if (/iPhone|iPad|iPod|Android/i.test(userAgent)) return true
  return /Mac/.test(userAgent) && touchPoints > 1
}

/** Local encoding is offered only where one pass cannot take the tab down with it. */
export function localEncodeViable(device: string): boolean {
  return device === 'webgpu' && !isMobileDevice() && !inSafeMode()
}

/**
 * The crash-loop damper (set by main.tsx): three page loads inside 45 seconds means the page
 * is being reloaded by something other than a person - almost certainly the browser reviving
 * a killed tab - so the heaviest subsystem stays off for the rest of the browser session.
 * Everything still works; taps segment on the server instead.
 */
export function inSafeMode(): boolean {
  try {
    return sessionStorage.getItem('asb-safe-mode') === '1'
  } catch {
    return false
  }
}

type Waiter = { resolve: (value: never) => void; reject: (error: Error) => void }

class Segmenter {
  private worker: Worker | null = null
  private waiters: Waiter[] = []
  private encodedPhoto: string | null = null
  private liveFrame = 0
  state: SegmentState = 'idle'
  device = 'wasm'
  onStateChange: ((state: SegmentState) => void) | null = null

  private setState(next: SegmentState): void {
    this.state = next
    this.onStateChange?.(next)
  }

  private ensureWorker(): Worker {
    if (this.worker) return this.worker
    this.worker = new Worker(new URL('../workers/segment.worker.ts', import.meta.url), {
      type: 'module',
    })
    this.worker.onmessage = (event: MessageEvent<SegmentResponse>) => {
      const waiter = this.waiters.shift()
      if (!waiter) return
      const message = event.data
      if (message.type === 'error') {
        if (message.fatal) this.setState('unavailable')
        waiter.reject(new Error(message.message))
        return
      }
      if (message.type === 'ready') {
        this.device = message.device
        this.setState('ready')
      }
      waiter.resolve(message as never)
    }
    this.worker.onerror = () => {
      this.setState('unavailable')
      for (const waiter of this.waiters.splice(0)) {
        waiter.reject(new Error('segmentation is unavailable on this device'))
      }
    }
    return this.worker
  }

  private send<T extends SegmentResponse>(message: unknown, transfer: Transferable[] = []) {
    const worker = this.ensureWorker()
    return new Promise<T>((resolve, reject) => {
      this.waiters.push({ resolve: resolve as (value: never) => void, reject })
      worker.postMessage(message, transfer)
    })
  }

  /** Warm the model. Safe to call more than once; the second call is free. */
  async load(): Promise<boolean> {
    if (this.state === 'ready') return true
    if (this.state === 'unavailable') return false
    this.setState('loading')
    try {
      await this.send({ type: 'load' })
      return true
    } catch {
      this.setState('unavailable')
      return false
    }
  }

  /**
   * Run the image encoder once for a photo. This is the expensive half (~1–3 s on a phone in
   * WASM, ~300 ms on WebGPU); every tap afterwards is tens of milliseconds.
   */
  async prepare(photoId: string, source: Blob | ImageBitmap): Promise<boolean> {
    if (this.encodedPhoto === photoId) return true
    if (!(await this.load())) return false
    if (!localEncodeViable(this.device)) {
      // See localEncodeViable: WASM is 20+ s and gigabytes per encode, and even WebGPU kills
      // real phones. `unavailable` routes taps to the server fallback (WS5-04): the same
      // weights, ~5 s, and none of it on the device.
      this.setState('unavailable')
      return false
    }
    this.setState('encoding')
    try {
      const bitmap = source instanceof Blob ? await createImageBitmap(source) : source
      await this.send({ type: 'encode', photoId, bitmap }, [bitmap])
      this.encodedPhoto = photoId
      this.setState('ready')
      return true
    } catch {
      this.setState('ready')
      return false
    }
  }

  async segment(photoId: string, points: SegmentPoint[]): Promise<SegmentResult | null> {
    if (this.encodedPhoto !== photoId || points.length === 0) return null
    try {
      const message = await this.send<Extract<SegmentResponse, { type: 'decoded' }>>({
        type: 'decode',
        photoId,
        points,
      })
      return {
        alpha: new Uint8Array(message.mask),
        width: message.width,
        height: message.height,
        score: message.score,
        ms: message.ms,
      }
    } catch {
      return null
    }
  }

  /**
   * Encode one live frame and decode it at `points`, in one call (07 §2, WS5-02).
   *
   * `prepare` + `segment` exist because a still photo is encoded once and tapped many times.
   * A camera frame is the opposite: encoded once, decoded once, thrown away. Reusing `prepare`
   * would leak a new `photoId` per frame into `encodedPhoto` and make the still-photo path's
   * cache check meaningless, so this is a separate entry point that leaves that state alone.
   *
   * Returns null rather than throwing on any failure: the caller is a render loop, and a
   * dropped frame is not an error worth a state transition.
   */
  async segmentFrame(bitmap: ImageBitmap, points: SegmentPoint[]): Promise<SegmentResult | null> {
    if (!(await this.load())) return null
    const frameId = `live:${(this.liveFrame += 1)}`
    try {
      await this.send({ type: 'encode', photoId: frameId, bitmap }, [bitmap])
      const message = await this.send<Extract<SegmentResponse, { type: 'decoded' }>>({
        type: 'decode',
        photoId: frameId,
        points,
      })
      return {
        alpha: new Uint8Array(message.mask),
        width: message.width,
        height: message.height,
        score: message.score,
        ms: message.ms,
      }
    } catch (error) {
      // Logged, not swallowed. The caller is a loop that turns itself off on the first null,
      // and a feature that quietly stops with no reason in the console is one nobody can
      // diagnose from a bug report.
      console.warn('live segmentation failed', error)
      return null
    } finally {
      // The worker now holds this frame's embedding under `frameId`, not the still photo's, so
      // the next tap on the photo has to re-encode. Saying so is what keeps the two paths from
      // silently returning each other's masks.
      this.encodedPhoto = null
    }
  }

  dispose(): void {
    this.worker?.terminate()
    this.worker = null
    this.encodedPhoto = null
    this.waiters = []
    this.setState('idle')
  }
}

/** One per tab. The model is 14 MB; a second copy is 14 MB of a phone's memory for nothing. */
export const segmenter = new Segmenter()

// ------------------------------------------------------------------------ mask utilities
/** Alpha buffer → a PNG data payload the API accepts (base64, no data: prefix). */
export async function alphaToPngBase64(
  alpha: Uint8Array,
  width: number,
  height: number,
): Promise<string> {
  const canvas =
    typeof OffscreenCanvas !== 'undefined'
      ? new OffscreenCanvas(width, height)
      : Object.assign(document.createElement('canvas'), { width, height })
  const context = (canvas as HTMLCanvasElement).getContext('2d')
  if (!context) throw new Error('no 2d context')

  const image = context.createImageData(width, height)
  for (let i = 0; i < alpha.length; i += 1) {
    const on = alpha[i] ? 255 : 0
    image.data[i * 4] = on
    image.data[i * 4 + 1] = on
    image.data[i * 4 + 2] = on
    image.data[i * 4 + 3] = 255
  }
  context.putImageData(image, 0, 0)

  const blob =
    canvas instanceof OffscreenCanvas
      ? await canvas.convertToBlob({ type: 'image/png' })
      : await new Promise<Blob>((resolve, reject) =>
          (canvas as HTMLCanvasElement).toBlob(
            (result) => (result ? resolve(result) : reject(new Error('encode failed'))),
            'image/png',
          ),
        )

  const buffer = new Uint8Array(await blob.arrayBuffer())
  let binary = ''
  for (let i = 0; i < buffer.length; i += 1) binary += String.fromCharCode(buffer[i] ?? 0)
  return btoa(binary)
}

/** The bounding box and area of an alpha buffer, for a quick "is this worth sending?" check. */
export function describeAlpha(
  alpha: Uint8Array,
  width: number,
  height: number,
): { bbox: [number, number, number, number]; area: number } {
  let minX = width
  let minY = height
  let maxX = -1
  let maxY = -1
  let area = 0
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (!alpha[y * width + x]) continue
      area += 1
      if (x < minX) minX = x
      if (x > maxX) maxX = x
      if (y < minY) minY = y
      if (y > maxY) maxY = y
    }
  }
  if (maxX < 0) return { bbox: [0, 0, 0, 0], area: 0 }
  return { bbox: [minX, minY, maxX - minX + 1, maxY - minY + 1], area }
}

/**
 * Turn brush strokes into an alpha buffer — the fallback path, and the refinement tool.
 *
 * 07 §2 lists the brush as a fallback, but it is also what a homeowner reaches for when the
 * model grabs the mirror along with the vanity. Same output shape either way, so everything
 * downstream is identical.
 */
export function strokesToAlpha(
  strokes: { points: { x: number; y: number }[]; radius: number; erase: boolean }[],
  width: number,
  height: number,
): Uint8Array {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  if (!context) return new Uint8Array(width * height)

  context.lineCap = 'round'
  context.lineJoin = 'round'
  for (const stroke of strokes) {
    context.globalCompositeOperation = stroke.erase ? 'destination-out' : 'source-over'
    context.strokeStyle = '#fff'
    context.fillStyle = '#fff'
    context.lineWidth = stroke.radius * 2
    const [first, ...rest] = stroke.points
    if (!first) continue
    if (rest.length === 0) {
      context.beginPath()
      context.arc(first.x, first.y, stroke.radius, 0, Math.PI * 2)
      context.fill()
      continue
    }
    context.beginPath()
    context.moveTo(first.x, first.y)
    for (const point of rest) context.lineTo(point.x, point.y)
    context.stroke()
  }

  const image = context.getImageData(0, 0, width, height)
  const alpha = new Uint8Array(width * height)
  for (let i = 0; i < alpha.length; i += 1) alpha[i] = (image.data[i * 4 + 3] ?? 0) > 96 ? 255 : 0
  return alpha
}
